import "~/styles/pre-meeting.css"

import { Form, notification, TableColumnProps } from "antd"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLoaderData } from "react-router"

import { peopleSearch } from "~/api/pre-meeting"
import linkedinLogo from "~/assets/linkedin.png"
import ModalComponents from "~/containers/pre-meeting-brief/ModalComponents"
import PeopleResults from "~/containers/pre-meeting-brief/PeopleResults"
import PreMeetingBriefDetails from "~/containers/pre-meeting-brief/PreMeetingBriefDetails"
import SearchPanel from "~/containers/pre-meeting-brief/SearchPanel"
import { useGetPreviousPreMeetingBrief } from "~/hooks/pre-meeting"
import useGlobalStore from "~/store/global"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"
import { sanitizeUrl } from "~/utils/helpers"
import {
  classifyAccountId,
  isPmbV1,
  openPmbV2Workbench,
  PMB_V2_MISSING_LINKEDIN_MESSAGE
} from "~/containers/pre-meeting-brief-v2"

import {
  SearchLocations,
  SearchSeniorities,
  SENIORITY_OPTIONS
} from "./additional_filters"
import { EVENTS } from "./constants"
import OrganizationCell from "./organisation_cell"

interface FormData {
  primary_domain?: string
  id?: string
  name?: string
}

export const formatNamesForFile = (data: any) => {
  if (!data) return "NoNames"

  if (Array.isArray(data)) {
    const names = data.map((item) => item.name).filter(Boolean) // Extract names, removing falsy values
    if (names.length === 0) return "NoNames"
    if (names.length <= 2) return names.join(", ")
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`
  }

  return data.name || "NoNames"
}

export default function PreMeetingSearch() {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [notificationApi] = notification.useNotification()
  const loaderData = useLoaderData<any>()
  const tokenInfo = useGlobalStore((state) => state.tokenInfo)
  const [fetching, setFetching] = useState(false)
  const [people, setPeople] = useState<any>([])
  const [selectedPeople, setSelectedPeople] = useState<any>([])
  const [selectedRowKeys] = useState<any>([])
  const [searched, setSearched] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [openDrawer, setOpenDrawer] = useState(false)
  const [formData, setFormData] = useState<FormData | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [sessionId, setSessionId] = useState<string>(Date.now().toString())
  const timestamp = Date.now().toString()
  const [pendingRequests, setPendingRequests] = useState(0)
  const [formError, setFormError] = useState(false)
  const [linkedinSearch, setLinkedinSearch] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const getPeople = useCallback(
    async (
      values: {
        search?: string
        person_seniorities?: string[]
        person_locations?: string[]
      } | null,
      companyId: any
    ) => {
      setFetching(true)
      try {
        const name = values?.search || ""
        const person_seniorities = values?.person_seniorities || []
        const person_locations = values?.person_locations || []
        setPendingRequests((prev) => prev + 1)

        const response = await peopleSearch(
          tokenInfo,
          name,
          companyId,
          person_seniorities,
          person_locations
        )
        if ("error" in response) {
          throw new Error(String(response.error))
        }

        const json = await response.json()

        // On failure json.data is null (see createResponse in api-getorbitx) —
        // read the real backend message before touching .people, so the user
        // sees the actual reason (e.g. an Apollo rejection) instead of a
        // generic "Cannot read properties of null" crash message.
        if (!response.ok) {
          throw new Error(
            json?.error?.message || t("pre_meeting_brief.error_try_later")
          )
        }

        setPeople(json.data.people)
      } catch (error: any) {
        notificationApi.error({
          message: t("pre_meeting_brief.error_fetching_people"),
          description: error?.message || t("pre_meeting_brief.error_try_later")
        })
      } finally {
        setPendingRequests((prev) => Math.max(prev - 1, 0))
        setFetching(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tokenInfo, sessionId, selectedRowKeys]
  )

  const { refetch: refetchPreviousBrief } = useGetPreviousPreMeetingBrief(true)

  const handleSubmit = useCallback(
    async (values: any) => {
      setFormError(false)
      values.person_seniorities = values.person_seniorities?.flatMap(
        (key: string) => SENIORITY_OPTIONS[key].split(", ").map((v) => v.trim())
      )
      setSearchLoading(true)
      setSearched(false)
      setFormData(JSON.parse(values.pre_meeting_company || "{}"))
      let selectedCompanyId: string | null = null

      const company = values?.pre_meeting_company
        ? JSON.parse(values.pre_meeting_company)
        : null
      selectedCompanyId = company?.id || null

      const startTime = Date.now()

      try {
        setCurrentPage(1)
        await getPeople(values, selectedCompanyId)
        const timeTaken = (Date.now() - startTime) / 1000
        track(EVENTS.RESULT_GENERATED, {
          ...getBaseEventProps({
            feature: "pre_meeting_brief",
            accountId: selectedCompanyId || undefined,
            accountName: (() => {
              try {
                return JSON.parse(values?.pre_meeting_company)?.name || ""
              } catch {
                return ""
              }
            })()
          }),
          account_selected: (() => {
            try {
              return JSON.parse(values?.pre_meeting_company)?.name || ""
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (error) {
              return ""
            }
          })(),
          person_name: values.search,
          result_loaded_successfully: !!people,
          time_taken_to_generate: timeTaken,
          people_count: people?.length || 0
        })
        refetchPreviousBrief()
      } catch (err) {
        track(EVENTS.RESULT_GENERATED, {
          ...getBaseEventProps({
            feature: "pre_meeting_brief",
            accountId: selectedCompanyId || undefined,
            accountName: (() => {
              try {
                return JSON.parse(values?.pre_meeting_company)?.name || ""
              } catch {
                return ""
              }
            })()
          }),
          account_selected: (() => {
            try {
              return JSON.parse(values?.pre_meeting_company)?.name || ""
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (error) {
              return ""
            }
          })(),
          person_name: values.search,
          result_loaded_successfully: false,
          time_taken_to_generate: (Date.now() - startTime) / 1000,
          people_count: 0,
          error: err
        })
      }

      setSearchLoading(false)
      setSearched(true)
    },
    [getPeople, people, refetchPreviousBrief]
  )

  const peopleOptions = useMemo(() => {
    const selectedPeopleSet = new Set(
      selectedPeople.map((person: any) => person.id)
    )

    let combinedPeople = [...selectedPeople]

    const filteredSearchResults = people.filter(
      (person: any) => !selectedPeopleSet.has(person.id)
    )
    combinedPeople = [...selectedPeople, ...filteredSearchResults]

    return combinedPeople.map((item: any) => {
      const linkedin_url_raw = sanitizeUrl(
        item.linkedin_url_raw || item.linkedin_url
      )

      return {
        ...item,
        key: item.id,
        linkedin_url_raw,
        linkedin_url: (
          <a
            href={linkedin_url_raw}
            target="_blank"
            rel="noreferrer"
            className="flex"
          >
            <img src={linkedinLogo} alt="linkedin url" width={16} height={16} />
          </a>
        ),
        selected: selectedPeopleSet.has(item.id)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people])

  const initialValues = useMemo(() => {
    return {
      pre_meeting_company: loaderData?.sessionDetails?.clientDomain?.name
        ? JSON.stringify({
            id: loaderData?.sessionDetails?.clientDomain?.id,
            primary_domain: loaderData?.sessionDetails?.clientDomain?.domain
          })
        : undefined
    }
  }, [loaderData])

  // v2: only one person per brief — antd Table type="radio" swaps the
  // checkbox column for radios and auto-removes the "select all" header.
  // v1: legacy bulk-brief picker with a 10-person cap.
  const rowSelection = {
    type: (isPmbV1 ? "checkbox" : "radio") as "radio" | "checkbox",
    selectedRowKeys: selectedPeople.map((row: any) => row.id),
    onChange: (newSelectedRowKeys: any[], newSelectedRows: any[]) => {
      if (isPmbV1 && newSelectedRows.length > 10) {
        notification.error({
          message: t("pre_meeting_brief.error_generic"),
          description: t("pre_meeting_brief.error_max_10_people")
        })
        return
      }

      track(EVENTS.PERSON_SELECTION_CHANGED, {
        ...getBaseEventProps({
          feature: "pre_meeting_brief",
          accountId: formData?.id,
          accountName: formData?.name
        }),
        timestamp: Date.now(),
        account_selected: formData?.name,
        person_names: newSelectedRows.map((r) => r.name).join(", "),
        total_person: newSelectedRows.length,
        all_selected: isPmbV1 && newSelectedRows.length === people.length,
        multiple_selected: isPmbV1 && newSelectedRows.length > 1
      })
      setSelectedPeople(newSelectedRows)
    },
    onRowClick: (record: any) => {
      if (!isPmbV1) {
        // Radio behaviour: clicking the row sets selection to just this
        // record. Clicking the same row again is a no-op (radio can't be
        // toggled off the way a checkbox can).
        const isAlreadySelected = selectedPeople.some(
          (r: any) => r.id === record.id
        )
        if (isAlreadySelected) return
        rowSelection.onChange([record.id], [record])
        return
      }
      // v1 checkbox behaviour — toggle membership.
      const isAlreadySelected = selectedPeople.some(
        (r: any) => r.id === record.id
      )
      const newSelectedRows = isAlreadySelected
        ? selectedPeople.filter((r: any) => r.id !== record.id)
        : [...selectedPeople, record]

      rowSelection.onChange(
        newSelectedRows.map((r: any) => r.id),
        newSelectedRows
      )
    }
  }

  const columns: TableColumnProps[] = [
    {
      title: t("pre_meeting_brief.column_name"),
      dataIndex: "name",
      width: "20%",
      fixed: "left",
      render: (text: string, record: any) => (
        <div className="flex items-center space-x-2">
          {record.linkedin_url && (
            <a
              href={record.linkedin_url_raw}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={linkedinLogo}
                alt="LinkedIn"
                width={16}
                height={16}
                className="shrink-0"
              />
            </a>
          )}
          <span>{text}</span>
        </div>
      )
    },
    {
      title: t("pre_meeting_brief.column_title"),
      dataIndex: "title",
      width: "30%"
    },
    {
      title: t("pre_meeting_brief.column_account"),
      dataIndex: "organization_name",
      width: "25%",
      fixed: "left",
      render: (text: string, record: any) => (
        <OrganizationCell
          logo={record.organization_logo}
          name={text}
          hideLogo
        />
      )
    },
    {
      title: t("pre_meeting_brief.column_location"),
      dataIndex: "country",
      width: "15%"
    }
  ]

  const handleGenerateBriefButtonClick = async () => {
    try {
      const person = selectedPeople[0]
      const linkedinUrl =
        person?.linkedin_url_raw ??
        (typeof person?.linkedin_url === "string"
          ? person.linkedin_url
          : null) ??
        person?.linkedinUrl
      const accountId =
        formData?.id ?? person?.organization_id ?? person?.account_id

      track(EVENTS.VIEW_PRE_MEETING_BRIEF_CLICKED, {
        ...getBaseEventProps({
          feature: "pre_meeting_brief",
          accountId,
          accountName: formData?.name ?? person?.organization_name
        }),
        timestamp: Date.now(),
        account_selected: formData?.name ?? person?.organization_name,
        person_names: selectedPeople.map((r: any) => r.name).join(", "),
        total_person: selectedPeople.length,
        flow: isPmbV1 ? "pmb_v1_drawer" : "pmb_v2_askos"
      })

      if (isPmbV1) {
        // v1 — open the legacy PreMeetingBriefDetails drawer mounted at
        // the bottom of this file. `selectedPeople` is the multi-person
        // picker payload (v1 supports bulk briefs).
        setSessionId(Date.now().toString())
        setShowDetails(true)
        setOpenDrawer(true)
        return
      }

      // PMB v2 — route through ASKOS instead of opening the legacy drawer.
      // Bulk briefs were dropped at V2; we take the first selected person
      // and let reps iterate via chat.
      if (!linkedinUrl) {
        notification.error({
          message: t("pre_meeting_brief.error_generic"),
          description: PMB_V2_MISSING_LINKEDIN_MESSAGE
        })
        return
      }

      if (selectedPeople.length > 1) {
        notification.info({
          message: "Generating brief for the first selected person",
          description:
            "PMB v2 supports one person per brief — use chat to iterate to others."
        })
      }

      // Two separate id channels: numeric internal `account_id` vs Apollo
      // ObjectId (24-hex). classifyAccountId routes them; the agent's tool
      // reads both off the message context.
      const { accountId: routedAccountId, apolloId } =
        classifyAccountId(accountId)
      openPmbV2Workbench({
        linkedinUrl,
        accountId: routedAccountId ?? null,
        apolloId: apolloId ?? null,
        personName: person?.name ?? null,
        accountName: formData?.name ?? person?.organization_name ?? null,
        source: "knowledge_os_pre_meeting"
      })
    } catch (error: any) {
      notification.error({
        message: t("pre_meeting_brief.error_generic"),
        description:
          error?.message || t("pre_meeting_brief.error_something_went_wrong")
      })
    }
  }

  const handleValuesChange = (changedValues: any, values: any) => {
    setFormError(false)
    if (changedValues.search !== undefined) {
      const val = changedValues.search || ""
      setLinkedinSearch(val.toLowerCase().includes("linkedin.com/in/"))
    }
    if ("pre_meeting_company" in changedValues) {
      track(EVENTS.ACCOUNT_SELECTION, {
        ...getBaseEventProps({
          feature: "pre_meeting_brief",
          accountId: (() => {
            try {
              return JSON.parse(values?.pre_meeting_company)?.id || ""
            } catch {
              return ""
            }
          })(),
          accountName: (() => {
            try {
              return JSON.parse(values?.pre_meeting_company)?.name || ""
            } catch {
              return ""
            }
          })()
        }),
        timestamp: Date.now(),
        account_selected: (() => {
          try {
            return JSON.parse(values?.pre_meeting_company)?.name || ""
          } catch (error) {
            return `${error}`
          }
        })()
      })
    }

    if ("search" in changedValues) {
      const searchValue = changedValues.search.trim()
      if (
        values.pre_meeting_company &&
        (searchValue.length >= 3 ||
          ((values.person_seniorities?.length > 0 ||
            values.person_locations?.length > 0) &&
            searchValue.length === 0))
      ) {
        track(EVENTS.PEOPLE_SEARCH, {
          ...getBaseEventProps({
            feature: "pre_meeting_brief",
            accountId: (() => {
              try {
                return JSON.parse(values?.pre_meeting_company)?.id || ""
              } catch {
                return ""
              }
            })(),
            accountName: (() => {
              try {
                return JSON.parse(values?.pre_meeting_company)?.name || ""
              } catch {
                return ""
              }
            })()
          }),
          timestamp: Date.now(),
          account_selected: (() => {
            try {
              return JSON.parse(values?.pre_meeting_company)?.name || ""
            } catch (error) {
              return `${error}`
            }
          })(),
          search_query: searchValue
        })
      }
    }

    if ("person_seniorities" in changedValues) {
      const seniorities = changedValues.person_seniorities
      if (seniorities?.length >= 0 && values.pre_meeting_company) {
        track(EVENTS.SENIORITY_SEARCH, {
          ...getBaseEventProps({
            feature: "pre_meeting_brief",
            accountId: (() => {
              try {
                return JSON.parse(values?.pre_meeting_company)?.id || ""
              } catch {
                return ""
              }
            })(),
            accountName: (() => {
              try {
                return JSON.parse(values?.pre_meeting_company)?.name || ""
              } catch {
                return ""
              }
            })()
          }),
          timestamp: Date.now(),
          account_selected: (() => {
            try {
              return JSON.parse(values?.pre_meeting_company)?.name || ""
            } catch (error) {
              return `${error}`
            }
          })(),
          search_query: seniorities
        })
      }
    }

    if ("person_locations" in changedValues) {
      const locations = changedValues.person_locations
      if (locations?.length >= 0 && values.pre_meeting_company) {
        track(EVENTS.LOCATION_SEARCH, {
          ...getBaseEventProps({
            feature: "pre_meeting_brief",
            accountId: (() => {
              try {
                return JSON.parse(values?.pre_meeting_company)?.id || ""
              } catch {
                return ""
              }
            })(),
            accountName: (() => {
              try {
                return JSON.parse(values?.pre_meeting_company)?.name || ""
              } catch {
                return ""
              }
            })()
          }),
          timestamp: Date.now(),
          account_selected: (() => {
            try {
              return JSON.parse(values?.pre_meeting_company)?.name || ""
            } catch (error) {
              return `${error}`
            }
          })(),
          search_query: locations
        })
      }
    }
  }

  return (
    <>
      <div className="h-full min-w-[340px]">
        <SearchPanel
          form={form}
          formError={formError}
          linkedinSearch={linkedinSearch}
          initialValues={initialValues}
          handleSubmit={handleSubmit}
          handleValuesChange={handleValuesChange}
          SearchSeniorities={SearchSeniorities}
          SearchLocations={SearchLocations}
        />
      </div>

      <PeopleResults
        peopleOptions={peopleOptions}
        fetching={fetching}
        columns={columns}
        rowSelection={rowSelection}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        formData={formData}
        people={people}
        selectedPeople={selectedPeople}
        setSelectedPeople={setSelectedPeople}
        handleGenerateBriefButtonClick={handleGenerateBriefButtonClick}
        searchLoading={searchLoading}
        searched={searched}
        pendingRequests={pendingRequests}
      />

      {showDetails && (
        <PreMeetingBriefDetails
          data={{ ...formData }}
          urls={selectedPeople}
          open={openDrawer}
          setOpen={setOpenDrawer}
          sessionId={sessionId}
          timestamp={timestamp}
        />
      )}
      <ModalComponents />
    </>
  )
}

export const isValidNewsItem = (item: any): boolean => {
  if (!item || typeof item !== "object") return false

  const requiredKeys: (keyof typeof item)[] = ["link", "title", "date", "type"]

  return requiredKeys.every(
    (key) => typeof item[key] === "string" && item[key].trim().length > 0
  )
}

export const isValidPriorityItem = (item: any): boolean => {
  if (!item || typeof item !== "object") return false

  const requiredKeys: (keyof typeof item)[] = [
    "account_id",
    "id",
    "name",
    "user_id"
  ]

  return requiredKeys.every(
    (key) => item[key] !== null && item[key] !== undefined
  )
}
